import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiLockAlt } from 'react-icons/bi';
import { updateInstallment } from '@/api/accounts.api';
import { ApiError } from '@/api/errors';
import { StatusChip } from '@/components/common/StatusChip';
import { formatCurrencyEGP, formatDate } from '@/lib/format';
import { isInstallmentLocked, toneForInstallmentStatus } from '@/lib/accountStatus';
import type {
  AccountResponse,
  InstallmentRow as InstallmentRowType,
  InstallmentStatus,
  UpdateInstallmentRequest,
} from '@/types/shared';
import './InstallmentCard.css';

interface PaymentLocalState {
  amountPaid: string;
  paymentDate: string;
  paymentMethod: string;
  receiptNumber: string;
}

function toPaymentLocal(row: InstallmentRowType | undefined): PaymentLocalState {
  return {
    amountPaid: row?.amountPaid != null ? row.amountPaid.toFixed(2) : '',
    paymentDate: row?.paymentDate ? row.paymentDate.slice(0, 10) : '',
    paymentMethod: row?.paymentMethod ?? '',
    receiptNumber: row?.receiptNumber ?? '',
  };
}

interface InstallmentCardProps {
  studentCode: string;
  installmentNo: number;
  /** Draft plan amount/dueDate — lives in the parent's plan-editor state, saved together with
   * `totalAgreedFees`/`installmentCount` via the page's single "Save plan" action (D6). */
  amount: string;
  dueDate: string;
  /** The server's current row for this installment — `undefined` for a row just added client-side
   * (via the installment-count stepper) that hasn't been saved yet, so there's nothing to record a
   * payment against. */
  canonical: InstallmentRowType | undefined;
  canEdit: boolean;
  /** Disables the amount/dueDate plan inputs while the whole-plan save is in flight. */
  planSaving: boolean;
  onAmountChange: (value: string) => void;
  onDueDateChange: (value: string) => void;
  /** Called with the fresh `AccountResponse` after this row's own payment PATCH succeeds. */
  onPaymentSaved: (updated: AccountResponse) => void;
}

/** One installment's plan fields (amount/dueDate, feeding the page-level D6 plan save) plus its
 * own independently-saved payment panel (amountPaid/paymentDate/paymentMethod/receiptNumber) —
 * mirrors `student-affairs/DocumentRow.tsx`'s per-row independent save pattern. A6: once
 * `status === 'Paid'` the amount/dueDate inputs lock; payment metadata always stays editable.
 *
 * The card is a collapsible disclosure: a clickable header (installment no., status chip, amount,
 * and a compact due/paid hint) toggles the body (the editable fields + payment panel) via
 * `aria-expanded`/`aria-controls`, so a plan with several installments reads as a tidy list.
 * Collapsed by default; auto-expands if this row develops a validation/save error so the user can
 * see it without hunting for which card failed. */
export function InstallmentCard({
  studentCode,
  installmentNo,
  amount,
  dueDate,
  canonical,
  canEdit,
  planSaving,
  onAmountChange,
  onDueDateChange,
  onPaymentSaved,
}: InstallmentCardProps) {
  const { t, i18n } = useTranslation();
  const baseId = `installment-${installmentNo}`;
  const bodyId = `${baseId}-body`;
  const status: InstallmentStatus = canonical?.status ?? 'Pending';
  const locked = isInstallmentLocked(status);

  const [expanded, setExpanded] = useState(false);
  const [local, setLocal] = useState<PaymentLocalState>(() => toPaymentLocal(canonical));
  const [fieldErrors, setFieldErrors] = useState<{ amountPaid?: string; paymentDate?: string }>({});
  const [rowError, setRowError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const busy = saving;

  // Resync only when this row's own canonical data changes (initial load, or after this row's own
  // successful save) — see AccountsDetailPage.handleInstallmentSaved, which preserves other rows'
  // object references so their cards don't lose in-progress edits when this one saves.
  useEffect(() => {
    setLocal(toPaymentLocal(canonical));
  }, [canonical]);

  // Auto-expand a collapsed card the moment it develops a validation/save error, so the user
  // doesn't have to guess which of several collapsed installments needs attention.
  useEffect(() => {
    if (rowError || fieldErrors.amountPaid || fieldErrors.paymentDate) {
      setExpanded(true);
    }
  }, [rowError, fieldErrors]);

  const initial = toPaymentLocal(canonical);
  const paymentDirty =
    local.amountPaid !== initial.amountPaid ||
    local.paymentDate !== initial.paymentDate ||
    local.paymentMethod !== initial.paymentMethod ||
    local.receiptNumber !== initial.receiptNumber;

  const amountNumber = Number(amount);
  let hint = '';
  if (status === 'Paid' && canonical?.paymentDate) {
    hint = t('accounts.detail.installments.paidHint', {
      date: formatDate(canonical.paymentDate, i18n.language),
    });
  } else if (status === 'Partially Paid' && canonical) {
    const remaining = Math.max(
      0,
      (Number.isNaN(amountNumber) ? 0 : amountNumber) - (canonical.amountPaid ?? 0),
    );
    hint = t('accounts.detail.installments.remainingHint', {
      amount: formatCurrencyEGP(remaining, i18n.language),
    });
  } else if (dueDate) {
    hint = t('accounts.detail.installments.dueHint', { date: formatDate(dueDate, i18n.language) });
  }

  async function handleSavePayment() {
    if (!canonical) return;
    setRowError(null);
    setSavedFlash(false);

    const amountPaidChanged = local.amountPaid !== initial.amountPaid;
    const paymentDateChanged = local.paymentDate !== initial.paymentDate;
    const errors: { amountPaid?: string; paymentDate?: string } = {};
    if (amountPaidChanged || paymentDateChanged) {
      if (local.amountPaid.trim() === '' || Number.isNaN(Number(local.amountPaid))) {
        errors.amountPaid = t('accounts.detail.installments.errors.amountPaidRequired');
      }
      if (!local.paymentDate) {
        errors.paymentDate = t('accounts.detail.installments.errors.paymentDateRequired');
      }
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    try {
      const payload: UpdateInstallmentRequest = {};
      if (local.paymentMethod !== initial.paymentMethod) {
        payload.paymentMethod = local.paymentMethod.trim();
      }
      if (local.receiptNumber !== initial.receiptNumber) {
        payload.receiptNumber = local.receiptNumber.trim();
      }
      if (amountPaidChanged || paymentDateChanged) {
        payload.amountPaid = Number(local.amountPaid);
        payload.paymentDate = local.paymentDate;
        payload.markPaid = true;
      }
      const updated = await updateInstallment(studentCode, installmentNo, payload);
      onPaymentSaved(updated);
      setSavedFlash(true);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const errs: { amountPaid?: string; paymentDate?: string } = {};
      for (const detail of apiError.details) {
        if (detail.field === 'amountPaid') errs.amountPaid = detail.message;
        if (detail.field === 'paymentDate') errs.paymentDate = detail.message;
      }
      if (Object.keys(errs).length > 0) {
        setFieldErrors(errs);
      } else if (apiError.code === 'PLAN_ARCHIVED_READONLY') {
        setRowError(t('accounts.detail.plans.archivedReadonlyError'));
      } else {
        setRowError(apiError.message || t('accounts.detail.installments.errors.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={`fa-installment-card${locked ? ' fa-installment-card--locked' : ''}`}
      aria-readonly={locked}
    >
      <button
        type="button"
        className="fa-installment-card__header"
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="fa-installment-card__chevron" aria-hidden="true" />
        <span className="fa-installment-card__no">
          {t('accounts.detail.installments.installmentLabel', { no: installmentNo })}
        </span>
        <StatusChip
          label={t(`accounts.detail.installments.status.${status}`)}
          tone={toneForInstallmentStatus(status)}
        />
        <span className="fa-installment-card__amount">
          <bdi>
            {formatCurrencyEGP(Number.isNaN(amountNumber) ? 0 : amountNumber, i18n.language)}
          </bdi>
        </span>
        {hint && (
          <span className="fa-installment-card__hint">
            <bdi>{hint}</bdi>
          </span>
        )}
        {locked && (
          <span
            className="fa-installment-card__lock"
            aria-hidden="true"
            title={t('accounts.detail.installments.lockedHint')}
          >
            <BiLockAlt />
          </span>
        )}
      </button>
      {locked && (
        <span className="fa-visually-hidden">{t('accounts.detail.installments.lockedHint')}</span>
      )}

      <div id={bodyId} className="fa-installment-card__body" hidden={!expanded}>
        <div className="fa-field-row">
          <div className="fa-field">
            <label htmlFor={`${baseId}-amount`}>
              {t('accounts.detail.installments.columns.amount')}
            </label>
            <input
              id={`${baseId}-amount`}
              type="number"
              min="0.01"
              step="0.01"
              className="fa-ltr-isolate"
              value={amount}
              disabled={!canEdit || locked || planSaving}
              onChange={(e) => onAmountChange(e.target.value)}
            />
          </div>
          <div className="fa-field">
            <label htmlFor={`${baseId}-dueDate`}>
              {t('accounts.detail.installments.columns.dueDate')}
            </label>
            <input
              id={`${baseId}-dueDate`}
              type="date"
              value={dueDate}
              disabled={!canEdit || locked || planSaving}
              onChange={(e) => onDueDateChange(e.target.value)}
            />
          </div>
        </div>

        <div className="fa-installment-card__payment">
          <h3 className="fa-installment-card__payment-title">
            {t('accounts.detail.installments.paymentTitle')}
          </h3>
          {!canonical ? (
            <p className="fa-field__hint">{t('accounts.detail.installments.unsavedHint')}</p>
          ) : (
            <>
              <div className="fa-field-row">
                <div className="fa-field">
                  <label htmlFor={`${baseId}-amountPaid`}>
                    {t('accounts.detail.installments.columns.amountPaid')}
                  </label>
                  <input
                    id={`${baseId}-amountPaid`}
                    type="number"
                    min="0"
                    step="0.01"
                    className="fa-ltr-isolate"
                    value={local.amountPaid}
                    disabled={!canEdit || busy}
                    aria-invalid={Boolean(fieldErrors.amountPaid)}
                    onChange={(e) => setLocal((prev) => ({ ...prev, amountPaid: e.target.value }))}
                  />
                  {fieldErrors.amountPaid && (
                    <p className="fa-field__error">{fieldErrors.amountPaid}</p>
                  )}
                </div>
                <div className="fa-field">
                  <label htmlFor={`${baseId}-paymentDate`}>
                    {t('accounts.detail.installments.columns.paymentDate')}
                  </label>
                  <input
                    id={`${baseId}-paymentDate`}
                    type="date"
                    value={local.paymentDate}
                    disabled={!canEdit || busy}
                    aria-invalid={Boolean(fieldErrors.paymentDate)}
                    onChange={(e) => setLocal((prev) => ({ ...prev, paymentDate: e.target.value }))}
                  />
                  {fieldErrors.paymentDate && (
                    <p className="fa-field__error">{fieldErrors.paymentDate}</p>
                  )}
                </div>
                <div className="fa-field">
                  <label htmlFor={`${baseId}-paymentMethod`}>
                    {t('accounts.detail.installments.columns.paymentMethod')}
                  </label>
                  <input
                    id={`${baseId}-paymentMethod`}
                    type="text"
                    maxLength={50}
                    value={local.paymentMethod}
                    disabled={!canEdit || busy}
                    onChange={(e) =>
                      setLocal((prev) => ({ ...prev, paymentMethod: e.target.value }))
                    }
                  />
                </div>
                <div className="fa-field">
                  <label htmlFor={`${baseId}-receiptNumber`}>
                    {t('accounts.detail.installments.columns.receiptNumber')}
                  </label>
                  <input
                    id={`${baseId}-receiptNumber`}
                    type="text"
                    className="fa-ltr-isolate"
                    maxLength={50}
                    value={local.receiptNumber}
                    disabled={!canEdit || busy}
                    onChange={(e) =>
                      setLocal((prev) => ({ ...prev, receiptNumber: e.target.value }))
                    }
                  />
                </div>
              </div>
              {canEdit && (
                <div className="fa-form-actions">
                  <button
                    type="button"
                    className="fa-btn fa-btn--secondary fa-installment-card__save"
                    disabled={!paymentDirty || busy}
                    onClick={handleSavePayment}
                  >
                    {saving
                      ? t('accounts.detail.installments.saving')
                      : t('accounts.detail.installments.savePayment')}
                  </button>
                  {savedFlash && !paymentDirty && (
                    <span className="fa-installment-card__saved" role="status">
                      {t('accounts.detail.installments.saved')}
                    </span>
                  )}
                  {rowError && (
                    <p className="fa-field__error" role="alert">
                      {rowError}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
